// ===================================
// COLLAPSE COMPONENTS - SYNTAX-KORRIGIERT
// ===================================

// DOM Helper Functions
function query(selector, context = document) {
	return context.querySelector(selector)
}

function queryAll(selector, context = document) {
	return Array.from(context.querySelectorAll(selector))
}

// Event Emitter Implementation
class SyncEmitter2 {
	constructor() {
		this.events = {}
	}

	on(event, callback) {
		if (!this.events[event]) {
			this.events[event] = []
		}
		this.events[event].push(callback)
		return this
	}

	off(event, callback) {
		if (!this.events[event]) return this
		
		if (callback) {
			const index = this.events[event].indexOf(callback)
			if (index > -1) {
				this.events[event].splice(index, 1)
			}
		} else {
			this.events[event] = []
		}
		return this
	}

	emit(event, ...args) {
		if (!this.events[event]) return this
		
		this.events[event].forEach(callback => {
			try {
				callback.apply(this, args)
			} catch (error) {
				console.error('Event callback error:', error)
			}
		})
		return this
	}

	once(event, callback) {
		const onceWrapper = (...args) => {
			callback.apply(this, args)
			this.off(event, onceWrapper)
		}
		return this.on(event, onceWrapper)
	}
}

// Dependencies Check
function checkCollapseDependencies() {
	const missing = []
	
	if (typeof Tween === 'undefined') {
		missing.push('Tween/GSAP')
	}
	
	if (typeof Timeline === 'undefined') {
		missing.push('Timeline/GSAP')
	}
	
	if (missing.length > 0) {
		console.error('❌ Fehlende Dependencies für Collapse:', missing)
		console.log('💡 Stelle sicher, dass GSAP in vendor files geladen ist')
		return false
	}
	
	console.log('✅ Alle Dependencies für Collapse verfügbar')
	return true
}

// Collapse Class
class Collapse extends SyncEmitter2 {
	constructor(el) {
		super()
		
		if (!el) {
			throw new Error('Collapse: Element ist erforderlich')
		}
		
		this.el = el
		el._collapseInstance = this
		
		this.triggers = queryAll('.js-collapse-trigger', this.el)
		this.content = query('.js-collapse-content', this.el)
		this.focusables = queryAll('a,button,input,select,textarea', this.content)

		if (!this.content) {
			console.warn('⚠️ Collapse: .js-collapse-content nicht gefunden in', this.el)
			return
		}
		
		if (this.triggers.length === 0) {
			console.warn('⚠️ Collapse: .js-collapse-trigger nicht gefunden in', this.el)
			return
		}

		const group = this.el.closest('.js-collapse-group')
		this.siblings = group && queryAll('.js-collapse', group).filter(el => el != this.el)

		console.log('🔧 Collapse Setup:', {
			element: this.el,
			triggers: this.triggers.length,
			focusables: this.focusables.length,
			siblings: this.siblings ? this.siblings.length : 0
		})

		this.setupEventHandlers()
		this.initializeState()
	}

	setupEventHandlers() {
		const postOpen = () => {
			this.triggers.forEach(trigger => trigger.setAttribute('aria-expanded', 'true'))
			this.content.setAttribute('aria-hidden', 'false')
			
			if (this.focusables.length) {
				this.focusables.forEach(focusable => (focusable.tabIndex = 0))
				
				try {
					this.focusables[0].focus()
				} catch (error) {
					console.warn('Focus fehlgeschlagen:', error)
				}
			}
			
			this.el.dispatchEvent(new CustomEvent('collapse:opened', {
				bubbles: true,
				detail: { collapse: this }
			}))
		}

		this.on('open', () => {
			console.log('📖 Collapse öffnen:', this.el)
			
			if (typeof Tween === 'undefined') {
				console.error('❌ Tween nicht verfügbar für Collapse Animation')
				postOpen()
				return
			}

			Tween.set(this.content, {
				height: 'auto'
			})
			
			const height = this.content.getBoundingClientRect().height
			const dur = Math.min(800, Math.max(height / 2, 150)) / 1000
			
			console.log('🎬 Collapse Animation:', { height, duration: dur })
			
			Tween.from(this.content, dur, {
				height: 0,
				ease: 'power2.out',
				onComplete: postOpen
			})
			
			if (this.siblings) {
				this.siblings.forEach(el => {
					if (el._collapseInstance) {
						el._collapseInstance.emit('close')
					}
				})
			}
		})

		const postClose = () => {
			this.triggers.forEach(trigger => trigger.setAttribute('aria-expanded', 'false'))
			this.content.setAttribute('aria-hidden', 'true')
			
			if (this.focusables.length) {
				this.focusables.forEach(focusable => (focusable.tabIndex = -1))
			}
			
			this.el.dispatchEvent(new CustomEvent('collapse:closed', {
				bubbles: true,
				detail: { collapse: this }
			}))
		}

		this.on('close', () => {
			console.log('📕 Collapse schließen:', this.el)
			
			if (typeof Tween === 'undefined') {
				console.error('❌ Tween nicht verfügbar für Collapse Animation')
				postClose()
				return
			}

			const contentRect = this.content.getBoundingClientRect()
			const height = contentRect.height
			const dur = Math.min(800, Math.max(height / 2, 150)) / 1000
			
			console.log('🎬 Collapse Close Animation:', { height, duration: dur })
			
			Tween.to(this.content, dur, {
				height: 0,
				ease: 'power2.in',
				onComplete: postClose
			})
		})
		
		this.triggers.forEach((trigger, index) => {
			if (!trigger.hasAttribute('aria-expanded')) {
				trigger.setAttribute('aria-expanded', 'false')
			}
			
			trigger.addEventListener('click', (e) => {
				e.preventDefault()
				
				const isOpen = trigger.getAttribute('aria-expanded') === 'true'
				console.log('🖱️ Collapse Trigger geklickt:', { isOpen, trigger: index })
				
				this.emit(isOpen ? 'close' : 'open')
			})
			
			trigger.addEventListener('keydown', (e) => {
				if (e.key === 'Enter' || e.key === ' ') {
					e.preventDefault()
					trigger.click()
				}
			})
		})
	}

	initializeState() {
		const initiallyOpen = this.triggers.some(trigger => 
			trigger.getAttribute('aria-expanded') === 'true'
		)
		
		if (initiallyOpen) {
			this.triggers.forEach(trigger => trigger.setAttribute('aria-expanded', 'true'))
			this.content.setAttribute('aria-hidden', 'false')
			if (typeof Tween !== 'undefined') {
				Tween.set(this.content, { height: 'auto' })
			}
		} else {
			this.emit('close')
		}
	}
	
	open() {
		this.emit('open')
		return this
	}

	close() {
		this.emit('close')
		return this
	}

	toggle() {
		const isOpen = this.triggers[0] && this.triggers[0].getAttribute('aria-expanded') === 'true'
		this.emit(isOpen ? 'close' : 'open')
		return this
	}

	isOpen() {
		return this.triggers[0] && this.triggers[0].getAttribute('aria-expanded') === 'true'
	}

	destroy() {
		this.events = {}
		
		if (this.el._collapseInstance === this) {
			delete this.el._collapseInstance
		}
		
		console.log('🗑️ Collapse Instanz zerstört')
	}
}

// ScrollCollapse Class
class ScrollCollapse extends SyncEmitter2 {
	constructor(el) {
		super()
		
		if (!el) {
			throw new Error('ScrollCollapse: Element ist erforderlich')
		}
		
		this.el = el
		el._collapseInstance = this
		
		this.triggers = queryAll('.js-collapse-trigger', this.el)
		this.content = query('.js-collapse-content', this.el)
		this.focusables = queryAll('a,button,input,select,textarea', this.content)

		if (!this.content) {
			console.warn('⚠️ ScrollCollapse: .js-collapse-content nicht gefunden in', this.el)
			return
		}
		
		if (this.triggers.length === 0) {
			console.warn('⚠️ ScrollCollapse: .js-collapse-trigger nicht gefunden in', this.el)
			return
		}

		const group = this.el.closest('.js-collapse-group')
		this.siblings = group && queryAll('.js-collapse', group).filter(el => el != this.el)

		console.log('🔧 ScrollCollapse Setup:', {
			element: this.el,
			triggers: this.triggers.length,
			focusables: this.focusables.length,
			siblings: this.siblings ? this.siblings.length : 0
		})

		this.setupScrollEventHandlers()
		this.emit('close')
	}

	setupScrollEventHandlers() {
		const postOpen = () => {
			this.triggers.forEach(trigger => trigger.setAttribute('aria-expanded', 'true'))
			this.content.setAttribute('aria-hidden', 'false')
			
			if (this.focusables.length) {
				this.focusables.forEach(focusable => (focusable.tabIndex = 0))
				try {
					this.focusables[0].focus()
				} catch (error) {
					console.warn('Focus fehlgeschlagen:', error)
				}
			}
		}

		this.on('open', () => {
			console.log('📖 ScrollCollapse öffnen:', this.el)
			
			if (typeof Tween === 'undefined') {
				console.error('❌ Tween nicht verfügbar für ScrollCollapse Animation')
				postOpen()
				return
			}

			Tween.set(this.content, {
				height: 'auto'
			})
			
			const height = this.content.getBoundingClientRect().height
			const dur = Math.min(800, Math.max(height / 2, 150)) / 1000
			
			Tween.from(this.content, dur, {
				height: 0,
				ease: 'power2.out',
				onComplete: postOpen
			})
			
			if (this.siblings) {
				this.siblings.forEach(el => {
					if (el._collapseInstance) {
						el._collapseInstance.emit('close')
					}
				})
			}
		})

		const postClose = () => {
			this.triggers.forEach(trigger => trigger.setAttribute('aria-expanded', 'false'))
			this.content.setAttribute('aria-hidden', 'true')
			
			if (this.focusables.length) {
				this.focusables.forEach(focusable => (focusable.tabIndex = -1))
			}
		}

		this.on('close', (isBottom) => {
			console.log('📕 ScrollCollapse schließen:', { element: this.el, isBottom })
			
			if (typeof Tween === 'undefined' || typeof Timeline === 'undefined') {
				console.error('❌ GSAP nicht verfügbar für ScrollCollapse Animation')
				postClose()
				return
			}

			let triggerRect, contentRect
			
			try {
				triggerRect = this.triggers[1] ? this.triggers[1].getBoundingClientRect() : null
				contentRect = this.content.getBoundingClientRect()
			} catch (error) {
				console.error('Rect measurement failed:', error)
				postClose()
				return
			}

			const height = contentRect.height
			const dur = Math.min(800, Math.max(height / 2, 150)) / 1000
			const TL = new Timeline()
			
			if (isBottom && triggerRect) {
				const scrollTarget = window.scrollY - (triggerRect.top - contentRect.top)
				
				TL.to(window, dur, {
					scrollTo: { y: scrollTarget },
					ease: 'power2.inOut'
				})
			}
			
			TL.to(this.content, dur, {
				height: 0,
				ease: 'power2.in',
				onComplete: postClose
			}, isBottom ? dur * 0.3 : 0)
		})
		
		this.triggers.forEach((trigger, index) => {
			if (!trigger.hasAttribute('aria-expanded')) {
				trigger.setAttribute('aria-expanded', 'false')
			}
			
			trigger.addEventListener('click', (e) => {
				e.preventDefault()
				
				const isOpen = trigger.getAttribute('aria-expanded') === 'true'
				console.log('🖱️ ScrollCollapse Trigger geklickt:', { 
					isOpen, 
					trigger: index, 
					isBottom: index === 1 
				})
				
				this.emit(isOpen ? 'close' : 'open', index === 1)
			})
			
			trigger.addEventListener('keydown', (e) => {
				if (e.key === 'Enter' || e.key === ' ') {
					e.preventDefault()
					trigger.click()
				}
			})
		})
	}

	open() { 
		this.emit('open')
		return this
	}
	
	close() { 
		this.emit('close')
		return this
	}
	
	toggle() {
		const isOpen = this.triggers[0] && this.triggers[0].getAttribute('aria-expanded') === 'true'
		this.emit(isOpen ? 'close' : 'open')
		return this
	}
	
	isOpen() { 
		return this.triggers[0] && this.triggers[0].getAttribute('aria-expanded') === 'true'
	}
	
	destroy() {
		this.events = {}
		if (this.el._collapseInstance === this) delete this.el._collapseInstance
	}
}

// Initialization
function initCollapseComponents() {
	if (!checkCollapseDependencies()) {
		console.error('❌ Collapse Komponenten können nicht initialisiert werden')
		return { collapseInstances: [], scrollCollapseInstances: [] }
	}

	console.log('🎬 Initialisiere Collapse Komponenten')

	const collapseInstances = queryAll('.js-collapse').map((el, index) => {
		try {
			console.log(`🔧 Erstelle Collapse ${index + 1}:`, el)
			return new Collapse(el)
		} catch (error) {
			console.error('❌ Fehler beim Erstellen Collapse:', error, el)
			return null
		}
	}).filter(instance => instance !== null)

	const scrollCollapseInstances = queryAll('.js-scroll-collapse').map((el, index) => {
		try {
			console.log(`🔧 Erstelle ScrollCollapse ${index + 1}:`, el)
			return new ScrollCollapse(el)
		} catch (error) {
			console.error('❌ Fehler beim Erstellen ScrollCollapse:', error, el)
			return null
		}
	}).filter(instance => instance !== null)

	console.log('✅ Collapse Komponenten initialisiert:', {
		collapse: collapseInstances.length,
		scrollCollapse: scrollCollapseInstances.length
	})

	return { collapseInstances, scrollCollapseInstances }
}

// Debugging Functions
window.debugCollapseComponents = function() {
	console.group('🔍 Collapse Components Debug')
	
	const collapseElements = queryAll('.js-collapse')
	const scrollCollapseElements = queryAll('.js-scroll-collapse')
	
	console.log('Collapse Elemente:', collapseElements.length)
	console.log('ScrollCollapse Elemente:', scrollCollapseElements.length)
	
	collapseElements.forEach((el, index) => {
		const instance = el._collapseInstance
		console.log(`Collapse ${index}:`, {
			element: el,
			instance: !!instance,
			isOpen: instance ? instance.isOpen() : 'N/A'
		})
	})
	
	scrollCollapseElements.forEach((el, index) => {
		const instance = el._collapseInstance
		console.log(`ScrollCollapse ${index}:`, {
			element: el,
			instance: !!instance,
			isOpen: instance ? instance.isOpen() : 'N/A'
		})
	})
	
	console.groupEnd()
}

// Auto-Initialization
function startCollapseSystem() {
	if (document.readyState === 'loading') {
		document.addEventListener('DOMContentLoaded', function() {
			console.log('📄 DOM Ready - Initialisiere Collapse System')
			const instances = initCollapseComponents()
			
			window.Collapse = Collapse
			window.ScrollCollapse = ScrollCollapse
			window.collapseInstances = instances.collapseInstances
			window.scrollCollapseInstances = instances.scrollCollapseInstances
			console.log('✅ Collapse-Klassen global verfügbar gemacht')
			
			setTimeout(() => {
				if (typeof window.debugCollapseComponents === 'function') {
					window.debugCollapseComponents()
				}
			}, 1000)
		})
	} else {
		console.log('📄 DOM bereits geladen - Initialisiere Collapse System sofort')
		const instances = initCollapseComponents()
		
		window.Collapse = Collapse
		window.ScrollCollapse = ScrollCollapse
		window.collapseInstances = instances.collapseInstances
		window.scrollCollapseInstances = instances.scrollCollapseInstances
		console.log('✅ Collapse-Klassen global verfügbar gemacht')
		
		setTimeout(() => {
			if (typeof window.debugCollapseComponents === 'function') {
				window.debugCollapseComponents()
			}
		}, 1000)
	}
}

// Warten auf GSAP Dependencies
if (typeof Tween !== 'undefined' && typeof Timeline !== 'undefined') {
	startCollapseSystem()
} else {
	let attempts = 0
	const maxAttempts = 50
	
	const waitForGSAP = setInterval(() => {
		attempts++
		
		if (typeof Tween !== 'undefined' && typeof Timeline !== 'undefined') {
			clearInterval(waitForGSAP)
			console.log('✅ GSAP für Collapse geladen nach', attempts, 'Versuchen')
			startCollapseSystem()
		} else if (attempts >= maxAttempts) {
			clearInterval(waitForGSAP)
			console.error('❌ GSAP konnte nicht geladen werden nach', maxAttempts, 'Versuchen')
			console.log('🔄 Versuche Collapse ohne GSAP zu initialisieren...')
			startCollapseSystem()
		}
	}, 100)
}